import {
    fetchVisaRequirements,
    getVisaRequirementsState,
    setDateOfArrival,
    setError,
    setPaxCount,
    setProcessingSpeed,
    setVisaType
} from '@/src/store/slices/visaSlice/visaRequirementsSlice'
import { useCallback, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { getTravelDetails } from '@/src/store/slices/visaSlice/travelDetailsSlice'
import { isEmpty } from '@/src/utils/helper'
import { usePathname, useSearchParams } from 'next/navigation'
import { logError } from '@/src/utils/logError'
import { PaxFareTypeKeys } from '@/src/types/visa/visaRequirementsType'
import { Item } from '@/src/ct-design/lib/Dropdown/type'
import { DeviceType } from '@/src/types'
import useVisaFormValidation from '@/src/hooks/useVisaFormValidation'
import useVisaNavigation from '@/src/hooks/useVisaNavigation'
import { showNotification } from '@/src/store/slices/notificationSlice'
import { VISA_ERRORS } from '@/src/constants/error'
import { getDashboardBooking } from '@/src/store/slices/visaSlice/dashboardBookingSlice'

const useVisaRequirements = (deviceType: DeviceType = 'DESKTOP') => {
    const dispatch = useAppDispatch()
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const currentStep = searchParams.get('step')
    const isItineraryPage = pathname?.includes('/itinerary/') || false

    const { nationality, destination } = useAppSelector(getTravelDetails)
    const {
        isLoading,
        visaType,
        processingSpeed,
        dateOfArrival,
        paxDetails,
        fareRef,
        faq,
        questionAndAnswers,
        error
    } = useAppSelector(getVisaRequirementsState)
    const dashboardBooking = useAppSelector(getDashboardBooking)
    const { navigateToStep } = useVisaNavigation()
    const { clearError } = useVisaFormValidation()

    const { adults, children } = paxDetails
    const isDashboardPage =
        pathname?.includes('/dashboard') || pathname === '/dashboard'

    const getEstimatedPrice = useCallback(
        (paxType: 'ADT' | 'CHD', paxFareTypeKeys: PaxFareTypeKeys) => {
            const fareRefKey = paxFareTypeKeys[paxType]
            const pricingData = fareRef[fareRefKey]
            const { startingPrice } = pricingData
            return startingPrice
        },
        [fareRef]
    )
    const estimatedPrice = useMemo(() => {
        if (!isEmpty(processingSpeed) && processingSpeed && adults > 0) {
            const { paxFareTypeKeys } = processingSpeed
            const perAdultPrice = getEstimatedPrice('ADT', paxFareTypeKeys)
            const perChildPrice = getEstimatedPrice('CHD', paxFareTypeKeys)
            return adults * perAdultPrice + children * perChildPrice
        }
        return 0
    }, [processingSpeed, adults, children, getEstimatedPrice])

    let nationalityvalue = ''
    let destinationvalue = ''
    if (isDashboardPage) {
        nationalityvalue =
            dashboardBooking?.data?.travelers?.[0]?.Nationality.toUpperCase() ||
            ''
        destinationvalue =
            dashboardBooking?.data?.searchCriteria?.countryCode || ''
    } else {
        nationalityvalue = nationality.value
        if (isEmpty(nationalityvalue)) {
            nationalityvalue = searchParams.get('nationality') || ''
        }
        destinationvalue = destination.value
        if (isEmpty(destinationvalue)) {
            destinationvalue = searchParams.get('destination') || ''
        }
    }

    useEffect(() => {
        // Dashboard can be opened directly from bookings; fetch FAQ lazily when missing.
        if (isDashboardPage) {
            if (isEmpty(nationalityvalue) || isEmpty(destinationvalue) || isLoading) {
                return
            }

            if (!faq) {
                dispatch(
                    fetchVisaRequirements({
                        nationalityCode: nationalityvalue,
                        destinationCountryCode: destinationvalue,
                        language: 'en',
                        deviceType
                    })
                )
            }
            return
        }

        if (currentStep === '3' || currentStep === '4' || isItineraryPage) {
            return
        }

        if (isEmpty(nationalityvalue) || isEmpty(destinationvalue)) {
            navigateToStep({ step: 1 })
            return
        }

        // Fetch metadata if not loaded for visa home flow.
        if (visaType.options.length === 0 && !isLoading) {
            dispatch(
                fetchVisaRequirements({
                    nationalityCode: nationalityvalue,
                    destinationCountryCode: destinationvalue,
                    language: 'en',
                    deviceType
                })
            )
        }
    }, [
        currentStep,
        isItineraryPage,
        isDashboardPage,
        nationalityvalue,
        destinationvalue,
        faq,
        isLoading,
        visaType.options.length,
        dispatch,
        deviceType,
        navigateToStep
    ])

    useEffect(() => {
        if (error) {
            if (error === VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR) {
                setTimeout(() => navigateToStep({ step: 1 }), 2000)
            }
            dispatch(
                showNotification({
                    message: error,
                    type: 'ERROR'
                })
            )
            dispatch(setError(''))
        }
    }, [error])

    const visaList = useMemo(() => {
        try {
            if (Array.isArray(visaType.options)) {
                return visaType.options.map(({ visaType }, index) => {
                    return {
                        item: visaType,
                        key: `${index}`
                    }
                })
            }
            return []
        } catch (error) {
            logError('getVisaTypeList', error)
            return []
        }
    }, [visaType.options])

    const { selectedVisaType, processingSpeedsList } = useMemo(() => {
        const { selectedOption, options } = visaType
        const { processingSpeeds } = selectedOption || {}

        let processingSpeedsList: Item[] = []
        if (Array.isArray(processingSpeeds)) {
            processingSpeedsList = processingSpeeds.map(
                (processingSpeed, index) => {
                    return {
                        item: processingSpeed.type,
                        key: `${index}`
                    }
                }
            )
        }
        const selectedVisa = {
            type: selectedOption ? selectedOption.visaType : '',
            index: options?.findIndex(
                (option) => option.visaType === selectedOption?.visaType
            )
        }
        return {
            selectedVisaType: selectedVisa,
            processingSpeedsList
        }
    }, [visaType.selectedOption])

    const selectedProcessingSpeed = useMemo(() => {
        const selectedProcessingSpeed = {
            type: '',
            index: -1
        }
        if (
            !isEmpty(processingSpeed) &&
            Array.isArray(visaType.selectedOption?.processingSpeeds)
        ) {
            selectedProcessingSpeed.type = processingSpeed?.type || ''
            selectedProcessingSpeed.index =
                visaType.selectedOption.processingSpeeds.findIndex(
                    (speed) => speed.type === processingSpeed?.type
                )
        }
        return selectedProcessingSpeed
    }, [processingSpeed, visaType.selectedOption])

    const createItineraryPayload = () => {}

    const visaTypeChangeHandler = (item: Item) => {
        const index = Number(item.key)
        const selectedVisaType = visaType.options[index]
        const { processingSpeeds } = selectedVisaType

        dispatch(setVisaType(selectedVisaType))
        if (Array.isArray(processingSpeeds)) {
            dispatch(setProcessingSpeed(processingSpeeds[0]))
        } else {
            dispatch(setProcessingSpeed(null))
        }
    }

    const processingSpeedChangeHandler = (item: Item) => {
        const { selectedOption } = visaType
        if (!isEmpty(selectedOption)) {
            const processingSpeedIndex = Number(item.key)
            const selectedProcessingSpeed =
                selectedOption?.processingSpeeds[processingSpeedIndex]
            if (selectedProcessingSpeed) {
                dispatch(setProcessingSpeed(selectedProcessingSpeed))
            }
        }
    }

    const handleSelectionChange = (
        type: 'visaType' | 'processingSpeed',
        item: Item
    ) => {
        try {
            clearError(type)
            switch (type) {
                case 'visaType':
                    visaTypeChangeHandler(item)
                    break
                case 'processingSpeed':
                    processingSpeedChangeHandler(item)
                    break

                default:
                    break
            }
        } catch (error) {
            logError('handleSelectionChange', error)
        }
    }

    const handleDateChange = (date: Date) => {
        const newDate = new Date(date.setHours(12, 0, 0, 0))
        dispatch(setDateOfArrival(newDate.toISOString()))
        clearError('dateOfArrival')
    }

    const handlePaxCountChange = (
        type: 'adults' | 'children',
        value: number
    ) => {
        const updatedPaxCount = {
            ...paxDetails,
            [type]: value
        }
        dispatch(setPaxCount(updatedPaxCount))
        clearError('paxDetails')
    }

    return {
        isLoading,
        processingSpeedsList,
        visaList,
        selectedVisaType,
        selectedProcessingSpeed,
        processingSpeed,
        dateOfArrival,
        adults,
        children,
        estimatedPrice,
        faq,
        questionAndAnswers,
        createItineraryPayload,
        handleDateChange,
        handleSelectionChange,
        handlePaxCountChange
    }
}

export default useVisaRequirements
